import React, { useEffect, useRef, useState, useCallback } from "react";
import { GameEngine } from "../game/GameEngine";
import { Input } from "../game/input";
import { Sound } from "../game/audio";
import { NetworkClient } from "../game/network";
import { COSMETIC_COLORS } from "../game/config";
import { HUD } from "../components/HUD";
import { MobileControls } from "../components/MobileControls";
import { EvolutionOverlay, LevelToast, KillFeed, RespawnOverlay, Countdown } from "../components/Overlays";
import { Wifi, WifiOff, Home, MessageCircle, Send, Copy, Check } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { api } from "../api/client";
import { toast } from "sonner";

const quickParam = () => { const q = new URLSearchParams(window.location.search).get("quick"); return q ? Math.max(8, parseInt(q) || 20) : null; };

const EMOTES = ["GG!", "Nice!", "LOL", "Watch out!", "GLHF", "Revenge!"];

export default function GameScreen({ profile, mode = "solo", partyCode = null, onMatchEnd, onQuit }) {
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const inputRef = useRef(null);
  const netRef = useRef(null);
  const endedRef = useRef(false);
  const isMP = mode === "online" || mode === "party" || mode === "champions";
  const { applyUser } = usePlayer();
  const [hud, setHud] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [levelToast, setLevelToast] = useState(null);
  const [kills, setKills] = useState([]);
  const [death, setDeath] = useState(null);
  const [countdown, setCountdown] = useState(isMP ? -1 : 3);
  const [combo, setCombo] = useState(null);
  const [status, setStatus] = useState(isMP ? "connecting" : "playing");
  const [netError, setNetError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatText, setChatText] = useState("");
  const [copied, setCopied] = useState(false);

  const sendChat = (text) => {
    const t = (text || "").trim().slice(0, 80);
    if (!t || !netRef.current) return;
    netRef.current.sendChat(t);
    setChatText("");
  };
  const copyInvite = async () => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/?party=${partyCode}`); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (e) { console.warn("copy failed", e); }
  };

  // Leaving early still banks the coins/XP earned so far.
  const quitMatch = async () => {
    if (endedRef.current) { onQuit(); return; }
    endedRef.current = true;
    const eng = engineRef.current;
    eng?.stop(); netRef.current?.close();
    try {
      if (eng && eng.player && (eng.player.score > 0 || eng.player.objects > 0)) {
        const { data } = await api.submitMatch(eng.snapshotResult());
        if (data.user) applyUser(data.user);
        const c = data.awarded?.coins || 0;
        if (c > 0) toast.success(`You banked ${c.toLocaleString()} coins for the run!`);
      }
    } catch (e) { console.warn("quit submit failed", e); }
    onQuit();
  };

  const isTouch = typeof window !== "undefined" && (window.matchMedia?.("(pointer: coarse)").matches || "ontouchstart" in window);

  const handleEvent = useCallback((type, payload) => {
    if (type === "evolution") { setEvolution(payload); setTimeout(() => setEvolution(null), 2400); }
    else if (type === "levelup") { setLevelToast(payload.level); setTimeout(() => setLevelToast(null), 1600); }
    else if (type === "kill") { setKills((k) => [...k.slice(-3), { id: Date.now() + Math.random(), name: payload.name }]); }
    else if (type === "combo") { setCombo({ combo: payload.combo, mult: payload.mult, id: Date.now() }); }
    else if (type === "death") { setDeath({ by: payload.by }); }
    else if (type === "respawn") { setDeath(null); }
    else if (type === "matchend") { endedRef.current = true; engineRef.current?.stop(); netRef.current?.close(); onMatchEnd({ ...payload, mode }); }
  }, [onMatchEnd, mode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
    };
    resize();
    window.addEventListener("resize", resize);

    const input = new Input();
    input.attach();
    inputRef.current = input;

    Sound.init();
    Sound.setEnabled(profile?.settings?.sound !== false);
    Sound.setMusic(profile?.settings?.music !== false);

    const skinColor = COSMETIC_COLORS[profile?.garage?.skin] || undefined;
    const baseOpts = {
      input, onHud: setHud, onEvent: handleEvent,
      theme: (profile?.garage?.skin || "skin_classic").replace("skin_", ""),
      playerName: profile?.displayName || "YOU",
      quality: profile?.settings?.quality || "high",
      cosmetics: {
        wheel: COSMETIC_COLORS[profile?.garage?.wheel],
        smash: COSMETIC_COLORS[profile?.garage?.smash],
        boost: COSMETIC_COLORS[profile?.garage?.boost],
        trail: COSMETIC_COLORS[profile?.garage?.trail],
      },
    };

    let cdInt;
    if (isMP) {
      const net = new NetworkClient();
      netRef.current = net;
      net.onClose = () => {
        if (endedRef.current) return;
        const eng = engineRef.current;
        // Live game already running -> degrade to a local match instead of kicking to menu.
        if (eng && eng.multiplayer) eng.goOffline();
        else if (!eng && mode === "party") setNetError("Could not reach the party server. Please try again.");
      };
      net.onChat = (m) => setMessages((x) => [...x.slice(-6), { id: Date.now() + Math.random(), name: m.name, text: m.text }]);
      net.connect({ name: profile?.displayName || "Player", color: skinColor || "#FF9500", code: partyCode || undefined, quick: quickParam() || undefined })
        .then((init) => {
          const engine = new GameEngine(canvas, { ...baseOpts, multiplayer: true, net, seed: init.seed, duration: init.duration });
          engineRef.current = engine;
          engine.start();
          setStatus("playing");
        })
        .catch(() => {
          if (mode === "party") { setNetError("Could not reach the party server. Please try again."); return; }
          // Offline fallback: PLAY should always deliver a match even if the live server is unreachable.
          netRef.current = null;
          const engine = new GameEngine(canvas, { ...baseOpts, duration: quickParam() || undefined });
          engineRef.current = engine;
          setStatus("playing");
          let cd = 3; setCountdown(cd);
          cdInt = setInterval(() => { cd -= 1; setCountdown(cd); if (cd <= 0) { clearInterval(cdInt); engine.start(); } }, 700);
        });
    } else {
      const engine = new GameEngine(canvas, { ...baseOpts, duration: quickParam() || undefined });
      engineRef.current = engine;
      let cd = 3; setCountdown(cd);
      cdInt = setInterval(() => { cd -= 1; setCountdown(cd); if (cd <= 0) { clearInterval(cdInt); engine.start(); } }, 700);
    }

    return () => {
      clearInterval(cdInt);
      window.removeEventListener("resize", resize);
      input.detach();
      engineRef.current?.stop();
      netRef.current?.close();
    };
  }, []); // eslint-disable-line

  return (
    <div className="fixed inset-0 overflow-hidden bg-black" data-testid="game-screen">
      <canvas ref={canvasRef} />
      {hud && <HUD hud={hud} onQuit={quitMatch} combo={combo} />}
      {isTouch && inputRef.current && status === "playing" && <MobileControls input={inputRef.current} />}
      {countdown > 0 && <Countdown value={countdown} />}
      {status === "connecting" && !netError && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/70" data-testid="mp-connecting">
          <Wifi size={48} className="text-cyan-400 animate-pulse" />
          <div className="font-head font-black text-2xl mt-4 text-white">Finding a Match...</div>
          <div className="text-slate-400 text-sm mt-1">Connecting to the SmashUp arena</div>
        </div>
      )}
      {netError && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/80 p-6 text-center" data-testid="mp-error">
          <WifiOff size={48} className="text-red-400" />
          <div className="font-head font-black text-2xl mt-4 text-white">Offline</div>
          <div className="text-slate-400 text-sm mt-1 max-w-xs">{netError}</div>
          <button onClick={onQuit} className="btn-primary px-8 py-3 mt-5 flex items-center gap-2" data-testid="mp-error-back"><Home size={18} /> Back to Menu</button>
        </div>
      )}
      {mode === "party" && partyCode && status === "playing" && (
        <button onClick={copyInvite} className="absolute top-16 left-1/2 -translate-x-1/2 z-40 card px-4 py-1.5 flex items-center gap-2 hover:border-amber-500/50" data-testid="party-code-badge">
          <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Party</span>
          <span className="font-head font-black text-lg text-amber-400 tracking-widest">{partyCode}</span>
          {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} className="text-slate-400" />}
        </button>
      )}

      {isMP && status === "playing" && (
        <div className="absolute bottom-3 left-3 z-40 w-64 max-w-[72vw] flex flex-col gap-1.5" data-testid="chat-panel">
          {(chatOpen || messages.length > 0) && (
            <div className="flex flex-col gap-1 max-h-40 overflow-y-auto scroll-thin" data-testid="chat-messages">
              {messages.map((m) => (
                <div key={m.id} className="pop-in bg-black/55 backdrop-blur-sm rounded-lg px-2.5 py-1 text-sm self-start max-w-full">
                  <span className="font-bold text-cyan-400">{m.name}: </span>
                  <span className="text-white break-words">{m.text}</span>
                </div>
              ))}
            </div>
          )}
          {chatOpen ? (
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap gap-1" data-testid="chat-emotes">
                {EMOTES.map((e) => (
                  <button key={e} onClick={() => sendChat(e)} className="bg-slate-800/80 hover:bg-slate-700 text-slate-200 text-[11px] font-bold px-2 py-1 rounded-lg" data-testid={`emote-${e}`}>{e}</button>
                ))}
              </div>
              <div className="flex gap-1.5">
                <input
                  value={chatText}
                  onChange={(e) => setChatText(e.target.value.slice(0, 80))}
                  onFocus={() => { if (inputRef.current) inputRef.current.enabled = false; }}
                  onBlur={() => { if (inputRef.current) inputRef.current.enabled = true; }}
                  onKeyDown={(e) => { if (e.key === "Enter") sendChat(chatText); }}
                  placeholder="Say something..."
                  className="flex-1 bg-black/60 border border-slate-700 rounded-lg px-2.5 py-1.5 text-white text-sm focus:border-cyan-500 outline-none"
                  data-testid="chat-input"
                />
                <button onClick={() => sendChat(chatText)} className="bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg px-2.5" data-testid="chat-send"><Send size={16} /></button>
                <button onClick={() => setChatOpen(false)} className="bg-slate-800 text-slate-400 rounded-lg px-2" data-testid="chat-close">×</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setChatOpen(true)} className="card px-3 py-1.5 flex items-center gap-2 self-start hover:border-cyan-500/50" data-testid="chat-open">
              <MessageCircle size={16} className="text-cyan-400" />
              <span className="text-xs font-bold text-slate-300 uppercase tracking-wide">Chat</span>
            </button>
          )}
        </div>
      )}

      {evolution && <EvolutionOverlay data={evolution} />}
      {levelToast && <LevelToast level={levelToast} />}
      <KillFeed kills={kills} />
      {hud?.boosting && <div className="speed-lines" data-testid="speed-lines" />}
      {death && <RespawnOverlay by={death.by} hud={hud} />}
    </div>
  );
}
